/**
 * Radio Group Input Component
 * ==========================
 * 
 * Form input template component for rendering radio button groups with validation.
 * This component is designed to be used within individual form components to provide
 * a consistent radio button interface with configurable orientation and styling.
 * 
 * Core Features:
 * - Radio button group with multiple options
 * - Configurable horizontal or vertical orientation
 * - Custom radio button styling (square with circular mark)
 * - Form validation integration with error display
 * - Responsive design with flexible layout options
 * - Touch state management for validation display
 * 
 * Input Properties:
 * - templateFormGroup: FormGroup containing the form controls
 * - control: AbstractControl for the specific radio group field
 * - label: Field label with required indicator
 * - options: Array of radio button options
 * - orientation: Layout direction (horizontal or vertical)
 * 
 * Validation Features:
 * - Required field validation
 * - Custom error messages for validation failures
 * - Touch state management for validation display
 * - Visual error states and styling
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';

@Component({
  selector: 'app-radio-group-input',
  template: `
    <ng-container *ngIf="templateFormGroup && control" [formGroup]="templateFormGroup">
      <ion-label [position]="position" class="form-label">
        <strong>{{ label }}:</strong>
        <ion-text *ngIf="requiredField" color="danger">*</ion-text>
      </ion-label>
      <ion-radio-group 
        [id]="controlName" 
        [formControl]="formControl"
        [class.ion-invalid]="hasError"
        [class.ion-touched]="control.touched"
        [errorText]="getErrorMessage()">
        <div [ngClass]="orientation === 'horizontal' ? 'radio-container-horizontal' : 'radio-container-vertical'">
          
            <ion-radio *ngFor="let option of options" lines="none" class="radio-item" [value]="option" labelPlacement="end" justify="start">{{ option }}</ion-radio>
      
        </div>
      </ion-radio-group>
      

  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
    
    .radio-item {
      flex: 1 1 0;
      min-width: 0;
      padding: 0 !important;
      padding-top: 10px;
      margin: 0 !important;
    }
    
    .radio-container-horizontal {
      display: flex;
      gap: 1rem;
      padding: 0.5rem 0;
      justify-content: space-between;
      width: 100%;
      margin-top: 10px;
    }

    .radio-container-vertical {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      width: 100%;
      margin-top: 10px;
    }

    .radio-container-horizontal .radio-item {
      flex: 1 1 0;
      min-width: 0;
      padding: 0 !important;
      margin: 0 !important;
    }
    
    .radio-container-vertical .radio-item {
      flex: unset;
      min-width: unset;
      width: 100%;
      padding: 0 !important;
      padding-top: 10px;
      margin: 0 !important;
    }
    
    ion-radio::part(container) {
  width: 30px;
  height: 30px;

  border-radius: 50%;
  border: 2px solid #ddd;
}

ion-radio::part(mark) {
  background: none;
  transition: none;
  transform: none;
  border-radius: 0;
}

ion-radio.radio-checked::part(container) {
  background: var(--ion-color-primary);
  border-color: transparent;
}

ion-radio.radio-checked::part(mark) {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  border: none;
  transform: none;
}

    
    /* Optional: Style invalid radio groups */
    ion-radio-group.ion-invalid.ion-touched {
      --border-color: var(--ion-color-danger);
    }
    
    /* Style invalid radio buttons */
    ion-radio-group.ion-invalid.ion-touched ion-radio {
      --color-checked: var(--ion-color-danger);
      --border-color-checked: var(--ion-color-danger);
    }

  `]
})
export class RadioGroupInputComponent implements OnChanges, OnInit {
  @Input() templateFormGroup!: FormGroup;
  @Input() control!: AbstractControl | null;
  @Input() label!: string;
  @Input() requiredField: boolean = false;
  @Input() options: Array<string> = [];
  @Input() fill: string = 'outline';
  @Input() inputClass: string = 'custom-radio-group';
  @Input() position: 'stacked' | 'fixed' | 'floating' = 'floating';
  /**
   * Orientation of the radio group: 'horizontal' (default) or 'vertical'.
   */
  @Input() orientation: 'horizontal' | 'vertical' = 'vertical';

  controlName: string = '';
  formControl: FormControl = new FormControl();

  /**
   * Computed property to check if control has errors and is touched.
   * Used to determine when to show error states and styling.
   * 
   * @returns True if the control has validation errors and has been touched
   */
  get hasError(): boolean {
    return !!(this.control?.invalid && this.control?.touched);
  }


  constructor() {}

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Sets up the control name and initializes the component.
   */
  ngOnInit(): void {
    if (this.control) {
      this.controlName = Object.keys(this.templateFormGroup.controls).find(
        key => this.templateFormGroup.get(key) === this.control
      ) || '';
    }
  }

  /**
   * Lifecycle hook that responds to input property changes.
   * Handles changes to templateFormGroup and control properties.
   * 
   * @param changes - Object containing information about changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if ('templateFormGroup' in changes) {
      this.templateFormGroup = changes['templateFormGroup'].currentValue;
    }
    if ('control' in changes && this.control) {
      this.controlName = Object.keys(this.templateFormGroup.controls).find(
        key => this.templateFormGroup.get(key) === this.control
      ) || '';
      this.formControl = this.control as FormControl;
    }
  }

  /**
   * Gets the appropriate error message based on validation errors.
   * Provides user-friendly error messages for different validation failures.
   * 
   * @returns String containing the appropriate error message
   */
  getErrorMessage(): string {
    if (!this.hasError || !this.control?.errors) {
      return '';
    }

    const errors = this.control.errors;

    if (errors['required']) {
      return `Please select an option`;
    }

    return `${this.label} is invalid`;
  }

}